import { useState } from "react";
import type { Channel } from "@/mocks/streams";
import { formatViewers, formatDuration } from "@/lib/format";

interface PlayerWindowProps {
  channel: Channel;
}

export default function PlayerWindow({ channel }: PlayerWindowProps) {
  const [noticeOpen, setNoticeOpen] = useState(false);

  return (
    <div>
      <div className="relative w-full aspect-video overflow-hidden rounded-xl bg-background-200">
        <img
          src={channel.cover}
          alt={`${channel.title} - ${channel.hostName} - 有因直播`}
          title={`${channel.title} ${channel.hostName}`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-background-50/55" />

        <div className="absolute left-4 top-4 flex items-center gap-2">
          {channel.isLive ? (
            <span className="flex items-center gap-1.5 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
              <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse" />
              直播中
            </span>
          ) : (
            <span className="rounded-md bg-secondary-800/90 px-2.5 py-1 text-xs text-foreground-600">已离线</span>
          )}
          <span className="flex items-center gap-1 rounded-md bg-background-50/70 px-2.5 py-1 text-xs text-foreground-600 backdrop-blur">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-eye-line text-xs"></i>
            </span>
            {formatViewers(channel.viewers)}
          </span>
        </div>

        <div className="absolute right-4 top-4">
          <span className="rounded-md bg-background-50/70 px-2.5 py-1 text-[11px] text-foreground-500 backdrop-blur">
            演示播放窗口
          </span>
        </div>

        <button
          type="button"
          onClick={() => setNoticeOpen(true)}
          className="absolute left-1/2 top-1/2 flex h-16 w-16 md:h-20 md:w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary-500/90 text-background-50 transition-transform hover:scale-105"
          aria-label="播放"
        >
          <i className="ri-play-fill text-3xl md:text-4xl"></i>
        </button>

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background-50/90 to-transparent px-4 pb-4 pt-10">
          <div className="h-1 w-full overflow-hidden rounded-full bg-background-300/60">
            <div className="h-full rounded-full bg-primary-500" style={{ width: "32%" }} />
          </div>
          <div className="mt-3 flex items-center justify-between text-foreground-700">
            <div className="flex items-center gap-4">
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-play-fill text-lg"></i>
              </span>
              <span className="text-xs">01:12 / 03:45</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-volume-up-line"></i>
              </span>
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-settings-3-line"></i>
              </span>
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-fullscreen-line"></i>
              </span>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-3 flex items-center gap-2 text-xs text-foreground-400">
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-information-line"></i>
        </span>
        本播放窗口为前端演示区域，暂未接入真实视频流，控件仅用于展示交互设计。
      </p>

      {noticeOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-background-50/75" onClick={() => setNoticeOpen(false)} />
          <div className="relative w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-6 text-center">
            <span className="mx-auto w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/12 text-primary-500">
              <i className="ri-live-line text-2xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">演示模式</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-400">
              当前为前端演示版本，尚未接入真实视频流，因此不会播放实际画面。你可以继续浏览主播信息、收藏频道并体验互动演示区域。
            </p>
            <button
              type="button"
              onClick={() => setNoticeOpen(false)}
              className="mt-5 w-full rounded-md bg-primary-500 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </div>
  );
}